import { WorkOrderChangeOrigin } from '@atlas/types'
import type { CommentEntry, WorkOrderCommentsProps } from '@/components/work-orders/WorkOrderComments'
import { ORIGIN_LABEL } from '@/domain/work-order-history'
import { useAuth } from '@/hooks/useAuth'
import { useUserNames } from '@/hooks/useUserNames'
import { useCreateComment, useDeleteComment, useWorkOrderTimeline } from '@/hooks/useWorkOrderTimeline'
import { canDeleteOwnedBy, useCan } from '@/lib/capabilities'

/**
 * A work order's comment thread wired to the live comment-service: author
 * UUIDs resolved to display names (names, not uuids — the assignee-picker
 * precedent), and the post/delete mutations. Comments are admin-only
 * (ADR-0012): the composer shows only for whoever may work the order, and is
 * also hidden if the API reports the caller can't post.
 */
export function useCommentThread(workOrderId: string) {
	const { entries, isLoading, commentsForbidden } = useWorkOrderTimeline(workOrderId)
	const nameById = useUserNames()
	const { canWork } = useCan()
	const { user } = useAuth()
	const createComment = useCreateComment(workOrderId)
	const deleteComment = useDeleteComment(workOrderId)

	const nameOf = (userId: string | null | undefined) => (userId ? nameById.get(userId) : undefined)
	const comments: CommentEntry[] = entries.flatMap((e) =>
		e.kind === 'comment'
			? [
					{
						...e,
						// An external comment has no ATLAS author: its name is the one the source recorded.
						who:
							(e.external
								? (e.external.authorName ?? ORIGIN_LABEL[WorkOrderChangeOrigin.WORKFRONT])
								: nameOf(e.authorId)) || 'System',
					},
				]
			: [],
	)

	return {
		comments,
		isLoading,
		canComment: !commentsForbidden && canWork,
		onComment: (input) => createComment.mutateAsync(input),
		onDeleteComment: (id) => deleteComment.mutateAsync(id),
		// The same ownership rule the server applies, asked per comment.
		// A comment from an external source is read-only for everyone, admins included.
		canDeleteComment: (entry) => !entry.external && canDeleteOwnedBy(user?.role, user?.userId, entry.authorId),
		isPosting: createComment.isPending,
	} satisfies WorkOrderCommentsProps
}
